import React from 'react';
import { Play, Star, ExternalLink, Zap, ArrowUpRight } from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import { useFilter } from '../../context/FilterContext';
import useReveal from '../../hooks/useReveal';
import { normalizeUrl, isTelegramLink, formatTelegramUrl, formatWhatsAppUrl } from '../../lib/utils';
import { resolveMediaUrl } from '../admin/MediaUpload';

export default function InPlay() {
  const { data } = useSite();
  const { query, matches: filterMatches } = useFilter();
  const matches = data.inPlayMatches || [];

  const q = (query || '').trim().toLowerCase();
  const filteredMatches = q
    ? matches.filter(filterMatches)
    : matches;

  return (
    <div id="get-id" className="rounded-xl bg-zinc-950/40 border border-zinc-900 overflow-hidden my-4" data-testid="get-id-section">
      <div className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-[color:var(--gold)]/15 to-transparent border-b border-zinc-900">
        <span className="w-6 h-6 rounded-full bg-[color:var(--gold)] text-black flex items-center justify-center font-black shadow-xs">
          <Play size={12} fill="currentColor" />
        </span>
        <span className="text-white font-extrabold text-sm md:text-base tracking-wide">GET ID</span>
      </div>

      <div className="hidden md:grid grid-cols-12 items-center px-4 py-2.5 border-b border-zinc-900 bg-zinc-950/60 text-xs font-semibold text-zinc-400">
        <div className="col-span-5">Platform</div>
        <div className="col-span-3">Category / Type</div>
        <div className="col-span-2 text-center">Rating</div>
        <div className="col-span-2 text-right">Action</div>
      </div>

      <div className="divide-y divide-zinc-900">
        {filteredMatches.length === 0 ? (
          <div className="py-8 text-center text-zinc-400 text-xs">
            No platforms matching "{query}"
          </div>
        ) : (
          filteredMatches.map((m, i) => (
            <Row
              key={m.id || i}
              m={m}
              delay={i * 40}
              data={data}
            />
          ))
        )}
      </div>
    </div>
  );
}

function Row({ m, delay, data }) {
  const { ref, visible } = useReveal();
  const rawVisit = (m.visitUrl || m.link || '').trim();
  const visitUrl = rawVisit ? normalizeUrl(rawVisit) : '';

  // Resolve GET ID Action URL (Telegram / WhatsApp)
  const itemAction = (m.actionLink || m.telegram || m.whatsapp || '').trim();
  let actionUrl = '#';

  if (itemAction) {
    if (isTelegramLink(itemAction)) {
      actionUrl = formatTelegramUrl(itemAction);
    } else if (itemAction.startsWith('wa.me') || itemAction.includes('whatsapp') || /^\+?\d{8,15}$/.test(itemAction.replace(/\s+/g, ''))) {
      actionUrl = formatWhatsAppUrl(itemAction, `I want ID for ${m.name}`);
    } else {
      actionUrl = formatTelegramUrl(itemAction);
    }
  } else {
    // Check global Telegram or WhatsApp
    const globalTg = (data?.telegramLink || (data?.social || {}).telegram || (data?.exchangeListings || {}).telegram || '').trim();
    const globalWa = ((data?.exchangeListings || {}).whatsapp || data?.whatsappLink || (data?.social || {}).whatsapp || '').trim();
    if (globalTg) {
      actionUrl = formatTelegramUrl(globalTg);
    } else if (globalWa) {
      const digits = globalWa.replace(/\D/g, '');
      actionUrl = `https://wa.me/${digits}?text=${encodeURIComponent(`I want ID for ${m.name}`)}`;
    } else if (visitUrl) {
      actionUrl = visitUrl;
    }
  }

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`match-row grid grid-cols-12 items-center px-3 md:px-4 py-3 gap-2 transition-all duration-500 hover:bg-zinc-900/40 ${
        visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'
      }`}
    >
      <div className="col-span-12 md:col-span-5 flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-md bg-zinc-900 border border-zinc-800 overflow-hidden flex items-center justify-center shrink-0">
          {m.logo ? (
            <img src={resolveMediaUrl(m.logo)} alt={m.name} className="max-w-full max-h-full object-contain" />
          ) : (
            <span className="text-[color:var(--gold)] font-extrabold text-sm">
              {(m.name || 'P').charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="text-white font-bold text-sm truncate">{m.name}</div>
            {m.rating && (
              <div className="md:hidden inline-flex items-center gap-1 shrink-0">
                <Star size={11} className="fill-[color:var(--gold)] text-[color:var(--gold)]" />
                <span className="text-white text-xs font-bold">{m.rating}</span>
              </div>
            )}
          </div>
          <div className="text-zinc-400 text-[11px] truncate">
            {m.type ? `${m.type} · ` : ''}{m.tags || ''}
          </div>
        </div>

        {/* Mobile Action Buttons: GET ID & Visit */}
        <div className="md:hidden flex items-center gap-1.5 shrink-0">
          {visitUrl && (
            <a
              href={visitUrl}
              target="_blank"
              rel="noreferrer"
              className="h-8 px-2 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-300 hover:text-white text-[11px] font-semibold flex items-center justify-center gap-1"
            >
              <ExternalLink size={11} /> Visit
            </a>
          )}
          <a
            href={actionUrl}
            target={actionUrl && actionUrl !== '#' ? '_blank' : '_self'}
            rel="noreferrer"
            className="h-8 px-3 rounded-md bg-[color:var(--gold)] hover:brightness-110 text-black text-[11px] font-black flex items-center justify-center gap-1 shadow-xs active:scale-95 transition"
          >
            <Zap size={11} className="fill-black shrink-0" />
            <span>GET ID</span>
          </a>
        </div>
      </div>

      <div className="hidden md:block col-span-3 text-zinc-300 text-xs font-semibold truncate">{m.type}</div>

      <div className="hidden md:flex col-span-2 items-center justify-center gap-1">
        <Star size={12} className="fill-[color:var(--gold)] text-[color:var(--gold)]" />
        <span className="text-white text-sm font-bold">{m.rating || '5.0'}</span>
        {m.reviews && <span className="text-zinc-500 text-[10px]">({m.reviews})</span>}
      </div>

      {/* Desktop Action Buttons: Visit Site (if set) & GET ID (Telegram / WhatsApp) */}
      <div className="hidden md:flex col-span-2 items-center justify-end gap-1.5">
        {visitUrl && (
          <a
            href={visitUrl}
            target="_blank"
            rel="noreferrer"
            className="h-8 px-2.5 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-300 hover:text-white text-xs font-semibold flex items-center justify-center gap-1 transition-colors"
          >
            <ExternalLink size={12} /> Visit Site
          </a>
        )}
        <a
          href={actionUrl}
          target={actionUrl && actionUrl !== '#' ? '_blank' : '_self'}
          rel="noreferrer"
          className="h-8 px-3.5 rounded-md bg-[color:var(--gold)] hover:brightness-110 text-black text-xs font-black flex items-center justify-center gap-1 shadow-xs active:scale-95 transition"
        >
          <Zap size={12} className="fill-black shrink-0" />
          <span>GET ID</span>
        </a>
      </div>
    </div>
  );
}
